import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import './Cart.css';

const Cart = () => {
  const { cartItems, removeFromCart, updateQuantity, getTotalPrice, clearCart } = useCart();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Reset "processing" state if the page is restored from bfcache (e.g. hitting back from Stripe)
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        setIsLoading(false);
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  const handleCheckout = async () => {
    if (cartItems.length === 0) {
      alert('Your cart is empty');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('/.netlify/functions/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: cartItems }),
      });

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('Checkout function unavailable. Please try again shortly.');
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to start checkout');
      }

      window.location.href = data.sessionUrl;
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="cart-page-container">
      <div className="cart-page">
        <Link to="/store" className="back-button">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 19L5 12L12 5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back to Store
        </Link>

        <h1>YOUR CART</h1>

        {cartItems.length === 0 ? (
          <div className="empty-cart-section">
            <p>Your cart is empty</p>
            <Link to="/store" className="continue-shopping-btn">
              Continue Shopping
            </Link>
          </div>
        ) : (
          <div className="cart-content">
            <div className="cart-items-section">
              <div className="cart-items-table">
                <div className="table-header">
                  <div className="header-col-image">Product</div>
                  <div className="header-col-size">Size</div>
                  <div className="header-col-price">Price</div>
                  <div className="header-col-qty">Quantity</div>
                  <div className="header-col-total">Total</div>
                  <div className="header-col-action">Action</div>
                </div>

                {cartItems.map((item) => (
                  <div key={`${item.productId}-${item.size}`} className="table-row">
                    <div className="col-image">
                      <img src={item.image} alt={item.name} />
                      <span>{item.name}</span>
                    </div>
                    <div className="col-size">{item.size}</div>
                    <div className="col-price">£{item.price.toFixed(2)}</div>
                    <div className="col-qty">
                      <div className="quantity-control">
                        <button
                          onClick={() => updateQuantity(item.productId, item.size, item.quantity - 1)}
                          className="qty-btn"
                        >
                          −
                        </button>
                        <span className="qty-display">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.productId, item.size, item.quantity + 1)}
                          className="qty-btn"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <div className="col-total">
                      £{(item.price * item.quantity).toFixed(2)}
                    </div>
                    <div className="col-action">
                      <button
                        onClick={() => removeFromCart(item.productId, item.size)}
                        className="remove-btn"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="cart-summary-section">
              <div className="summary-card">
                <h2>Order Summary</h2>
                <div className="summary-row">
                  <span>Subtotal:</span>
                  <span>£{getTotalPrice()}</span>
                </div>
                <div className="summary-row">
                  <span>Shipping:</span>
                  <span>Calculated at checkout</span>
                </div>
                <div className="summary-divider"></div>
                <div className="summary-row total">
                  <span>Total:</span>
                  <span>£{getTotalPrice()}</span>
                </div>

                <button className="checkout-btn" onClick={handleCheckout} disabled={isLoading}>
                  {isLoading ? 'Processing...' : 'PROCEED TO CHECKOUT'}
                </button>

                {error && <div className="error-message">{error}</div>}

                <Link to="/store" className="continue-shopping-link">
                  Continue Shopping
                </Link>

                <button className="clear-cart-btn" onClick={() => {
                  if (window.confirm('Are you sure you want to clear your cart?')) {
                    clearCart();
                  }
                }}>
                  Clear Cart
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
